/**
 * 지출결재 문서 모달 (v2.50.0 · v2.51.0 · v2.52.0 개정) — 보기 + 작성/수정 겸용.
 *
 * 제출은 **3종 모두 같은 흐름** (v2.52.0, 2026-08-04 사용자 확정):
 *   작성을 마치고 [확인] → 실제 제출본과 같은 PDF 미리보기 → **그 미리보기 안에서**
 *   [제출] 또는 [돌아가서 수정]. 모달 바깥에는 제출 버튼을 두지 않는다.
 *
 * 종류별 규칙:
 *   구입지급결의서 — 임시저장 없음. 부가세 포함가 기준(VAT 칸 없음).
 *     **견적서 첨부**(v2.73.0) — PDF·이미지·엑셀. 제출본 뒤에 참고자료로 붙는다.
 *   개인경비정산서 — 임시저장/제출 + 영수증(지출증빙) 첨부. **작성 화면에서 바로 첨부**
 *     (새 문서는 저장·제출할 때 함께 올라간다). 내 문서에서 [새 정산서(복사)] 가능.
 *   법인카드사용내역서 — 업로드 원본의 일자·사용처·금액은 잠금(행 추가·삭제 불가).
 *     **사용기간 = 귀속월 1일~말일 자동**(카드내역서는 1개월 단위 제출, v2.52.0).
 *     내용은 보조과목(계정과목별 분류+기타) → 사용대상 → 적요 3칸.
 *     사용대상: 복리후생비=직원 체크박스 / 차량유지비=차량번호 / 접대비=거래처 /
 *     여비교통비=목적지 / 그 외=생략(바로 적요). 영수증 없음.
 */

import { todayKst } from '../lib/date-label';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { ExpenseAttachBox } from './ExpenseAttachBox';
import { openServerPdfPreview, openPdfPreview, closePdfPreview } from '../lib/pdf-preview';
import { useAuth } from '../lib/auth-context';
import { DateInput } from './DateInput';
import { ImportOrderPicker } from './ImportOrderPicker';
import { SalesPickerModal } from './SalesPickerModal';
import { ItemPickModal } from './ItemPickModal';
import { ExpenseRemitModal, RemitStateLabel } from './ExpenseRemitModal';
import type { SalesListRow } from '../lib/types';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  docId: number | null;
  docType?: api.ExpenseDocType;
  onClose: () => void;
  /** 저장·결재 등 변경 후 목록 갱신용 */
  onChanged?: () => void;
  /** true 면 기존 문서를 바로 편집 모드로 (카드내역 [작성 시작] 등) */
  startInEdit?: boolean;
}

const fmtKrw = (n: number): string => Math.round(n).toLocaleString('ko-KR');

/**
 * 구입지급결의서 한 줄 — 수량·단가가 둘 다 있는데 금액이 수량×단가와 다르면 수량×단가를 돌려준다(같으면 null).
 * ⭐**경고만 한다 · 막지 않는다** (v2.229.3 · 사용자 결정 2026-10-06 — 점검 C#1).
 *   운영 17줄 중 다른 2줄이 둘 다 정확히 절반 금액이었다(반반 부담 같은 실제 쓰임) — 막으면 그 경우가 못 올라간다.
 */
function qtyPriceMismatch(qty: unknown, unitPrice: unknown, amount: unknown): number | null {
  const blank = (v: unknown): boolean => v === null || v === undefined || String(v).trim() === '';
  if (blank(qty) || blank(unitPrice)) return null;
  const q = Number(qty); const u = Number(unitPrice); const a = Number(amount);
  if (!Number.isFinite(q) || !Number.isFinite(u) || q === 0 || u === 0) return null;
  const calc = Math.round(q * u);
  return calc === Math.round(Number.isFinite(a) ? a : 0) ? null : calc;
}

/** 수량×단가 ≠ 금액 줄 안내 — 없으면 아무것도 안 그린다 (경고만 · 막지 않음) */
function QtyPriceWarn({ lines, note }: { lines: { no: number; calc: number | null; amount: number }[]; note: string }) {
  const bad = lines.filter((l) => l.calc !== null);
  if (bad.length === 0) return null;
  return (
    <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      {bad.map((l) => (
        <div key={l.no}>⚠ {l.no}번 줄: 수량×단가는 <b className="num">{fmtKrw(l.calc!)}</b>원인데 금액은 <b className="num">{fmtKrw(l.amount)}</b>원입니다.</div>
      ))}
      <div className="text-amber-700 mt-0.5">{note}</div>
    </div>
  );
}

export const EXPENSE_STATUS_BADGE: Record<string, string> = {
  작성중: 'bg-slate-100 text-slate-600 border-slate-200',
  검토대기: 'bg-amber-100 text-amber-800 border-amber-200',
  부서장승인: 'bg-sky-100 text-sky-800 border-sky-200',
  경영지원승인: 'bg-blue-100 text-blue-800 border-blue-200',
  최종승인: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  반려: 'bg-rose-100 text-rose-700 border-rose-200',
};

interface FormItem {
  key: number;
  itemDate: string;
  account: string;
  subAccount: string;
  /** 보조과목을 포털이 자동으로 채웠고 사람이 아직 안 바꿨다 (법인카드 · v2.227.0) — 「자동」 표시 · 저장된다 */
  subAuto: boolean;
  /** 왜 그렇게 채웠나 — 「자동」 표시의 말풍선. 저장하지 않는다(다시 열면 빈 글자 → 일반 안내) */
  subAutoWhy: string;
  usageTarget: string;
  vendor: string;
  description: string;
  qty: string;
  unitPrice: string;
  amount: string;
  note: string;
  /** 판매물품 줄의 영업건 (v2.246.0) — 빈 문자열 = 재고 보유 */
  salesId: string;
  /** 「2026-SM-0249 ○○사업」 — 화면 표기용(서버가 다시 만든다) */
  salesLabel: string;
  /**
   * 판매물품 줄의 매입 품목 (v2.304.0 · 개발요청 #16) — 기존 품목 코드 · 화면 표기(품명 · 그룹).
   *   목록에 없어 직접 입력한 품목은 newItem* 세 칸(코드는 최종승인 때 서버가 만든다). 둘 다 비면 품목 없음.
   */
  itemCode: string;
  itemLabel: string;
  newItemGroup: string;
  newItemName: string;
  newItemSpec: string;
}

/** 고른 품목의 표기 — 「품명 · 그룹」 */
function itemPickLabel(name: string | null | undefined, group: string | null | undefined): string {
  const n = (name ?? '').trim();
  const g = (group ?? '').trim();
  return n && g ? `${n} · ${g}` : n || g;
}

/** 영업건 고르기 창에서 고른 줄의 표기 — 서버 itemSalesLabel 과 같은 모양(사업명, 없으면 엔드유저) */
function salesRowLabel(r: SalesListRow): string {
  const name = (r.projectName ?? '').trim() || (r.endUser ?? '').trim();
  return name ? `${r.salesId} ${name}` : r.salesId;
}

interface FormState {
  docDate: string;
  purchaseType: string;
  /** 지출 분류 (v2.132.0) — 빈 문자열 = 아직 안 고름 */
  spendCategory: string;
  purpose: string;
  vendor: string;
  payMethod: string;
  /** 옛 「결제날짜」 글 — 입력칸은 없다(v2.267.0). 옛 문서를 고칠 때 받은 그대로 돌려보내 서버가 지우지 않게 */
  payDateText: string;
  /** 지급일 'YYYY-MM-DD' · 빈 문자열 = 비움 (v2.267.0 · 구입지급·개인경비) */
  payDate: string;
  bankHolder: string;
  bankName: string;
  bankAccount: string;
  cardNo: string;
  periodFrom: string;
  periodTo: string;
  items: FormItem[];
  /** 수출입관련 결의서의 수출입건 (v2.230.0 · 개발 요청 #14) — 수출입관련이 아니면 저장 때 비워진다 */
  importOrders: api.ImportOrderTag[];
}

let itemKeySeq = 1;
function newItem(): FormItem {
  return {
    key: itemKeySeq++,
    itemDate: '', account: '', subAccount: '', subAuto: false, subAutoWhy: '', usageTarget: '',
    vendor: '', description: '', qty: '', unitPrice: '', amount: '', note: '',
    salesId: '', salesLabel: '',
    itemCode: '', itemLabel: '', newItemGroup: '', newItemName: '', newItemSpec: '',
  };
}

function emptyForm(): FormState {
  return {
    docDate: todayKst(),
    purchaseType: '', spendCategory: '', purpose: '', vendor: '', payMethod: '', payDateText: '', payDate: '',
    bankHolder: '', bankName: '', bankAccount: '',
    cardNo: '', periodFrom: '', periodTo: '',
    items: [newItem()],
    importOrders: [],
  };
}

/**
 * 「작성 중인 내용이 바뀌었나」를 재는 지문 (v2.146.0).
 *
 * 왜: 작성 중에 창을 닫아 입력이 통째로 사라지는 사고가 있었다(사용자 2026-09-09).
 *     지금 폼과 **열었을 때의 폼**을 이 지문으로 비교해, 다르면 닫기 전에 물어본다.
 * ⚠`key` 는 화면에서 행을 구분하려고 붙인 일련번호(전역 카운터)라 값이 같아도 매번 달라진다 —
 *   빼고 잰다. 그러지 않으면 아무것도 안 고쳐도 「변경됨」 으로 읽힌다.
 */
function formFingerprint(f: FormState): string {
  return JSON.stringify({ ...f, items: f.items.map(({ key, ...rest }) => rest) });
}

/**
 * 법인카드 행 기본값 (v2.93.0 · 2026-08-25 사용자 지시) — **비어 있는 칸에만** 넣는다.
 *
 *   ① 사용처에 「주유소」 → 보조과목 주유 · 계정과목 차량유지비
 *      (v2.227.0: 사용처 글자 규칙 확대 — 주유·석유·에너지 / 스타벅스·카페… / 버거·김밥… — 그다음
 *       같은 사용처의 지난 기록(서버 hint · 3번 이상). 이렇게 채운 행은 subAuto=true → 「자동」 표시)
 *   ② 계정과목이 차량유지비면 → **사용대상**에 작성자에게 배정된 차량번호
 *      (2026-09-01: **비고**에는 안 넣는다 — 사용대상과 같은 값을 두 번 적는 셈이라 뺐다.
 *       예전 기본값으로 비고에 차량번호만 들어 있으면 그 칸을 비운다.
 *       ⚠사용자는 이 칸을 「적요」라 불렀지만 자동으로 채워지던 칸은 **비고(note)** 다 —
 *         적요(description)는 사람이 쓰는 칸이라 건드리지 않는다)
 *   ③ 계정과목이 복리후생비면 → 사용대상(함께한 직원)에 **본인**부터 (2026-08-25 사용자 지시)
 *
 * ⚠전부 그냥 기본값이라 손으로 고칠 수 있다. 같은 주유소에서 회식을 결제한 건도 있어서
 *   (운영 실측) 자동으로 정해 놓고 못 고치게 하면 안 된다.
 * ⚠이미 사람이 골라 둔 값은 건드리지 않는다 — 빈 칸에만 넣는다.
 * ⭐**언제 부르느냐가 더 중요하다** — 문서를 열 때는 **아직 아무도 분류하지 않은 행**에만
 *   부른다(detailToForm). 안 그러면 직원 선택 창에서 본인을 일부러 빼고 저장한 것을
 *   다음에 열 때 되살려 버린다. 보조과목을 새로 고르는 순간에는 늘 부른다 — 그 행을
 *   지금 분류하는 중이라 기본값을 주는 게 맞다.
 */
function withCardDefaults(
  it: FormItem, vehicleLabel: string | null, authorName: string | null,
  hint?: api.VendorHint | null,
): FormItem {
  const next = { ...it };
  if (!next.account && !next.subAccount) {
    const s = api.suggestSubByVendor(next.vendor, hint);
    if (s) {
      next.account = s.account; next.subAccount = s.sub;
      next.subAuto = true; next.subAutoWhy = s.why;
    }
  }
  if (vehicleLabel && next.account === '차량유지비') {
    if (!next.usageTarget.trim()) next.usageTarget = vehicleLabel;
    // 비고(note)에는 넣지 않는다 (2026-09-01 사용자 지시) — 차량번호는 사용대상 칸에 이미 있어
    // 같은 값을 두 번 적는 셈이었다. 비고는 사람이 쓸 말을 위해 비워 둔다.
    if (next.note.trim() === vehicleLabel) next.note = '';
  }
  if (authorName && next.account === '복리후생비' && !next.usageTarget.trim()) {
    next.usageTarget = authorName;
  }
  return next;
}

function detailToForm(d: api.ExpenseDocDetail, todayDate = false): FormState {
  const isCard = d.doc.docType === '법인카드사용내역서';
  const vehicleLabel = isCard ? (d.authorVehicle?.label ?? null) : null;
  // 「본인」 = 그 카드의 주인, 곧 문서 작성자 (경영지원이 대신 열어 봐도 작성자 기준)
  const authorName = isCard ? (d.doc.authorName?.trim() || null) : null;
  return {
    docDate: todayDate ? todayKst() : d.doc.docDate,
    purchaseType: d.doc.purchaseType ?? '',
    spendCategory: d.doc.spendCategory ?? '',
    purpose: d.doc.purpose ?? '',
    vendor: d.doc.vendor ?? '',
    payMethod: d.doc.payMethod ?? '',
    // 지급일은 그 문서의 날짜다 — [이 내용으로 새 정산서](todayDate)로 베낄 땐 비운다 (v2.267.0)
    payDateText: todayDate ? '' : (d.doc.payDateText ?? ''),
    payDate: todayDate ? '' : (d.doc.payDate ?? ''),
    bankHolder: d.doc.bankHolder ?? '',
    bankName: d.doc.bankName ?? '',
    bankAccount: d.doc.bankAccount ?? '',
    cardNo: d.doc.cardNo ?? '',
    periodFrom: d.doc.periodFrom ?? '',
    periodTo: d.doc.periodTo ?? '',
    // 지워진 오더(id 없음)는 다시 보낼 수 없어 뺀다 — 결재본(PDF)의 표기는 서버에 그대로 남아 있다
    importOrders: (d.importOrders ?? []).filter((o) => o.importOrderId !== null),
    items: d.items.length === 0 ? [newItem()] : d.items.map((it) => {
      const row: FormItem = {
        key: itemKeySeq++,
        itemDate: it.itemDate ?? '',
        account: it.account ?? '',
        subAccount: it.subAccount ?? '',
        subAuto: isCard && it.subAuto === true && !!it.subAccount,
        subAutoWhy: '',
        usageTarget: it.usageTarget ?? '',
        vendor: it.vendor ?? '',
        description: it.description ?? '',
        qty: it.qty === null || it.qty === undefined ? '' : String(it.qty),
        unitPrice: it.unitPrice === null || it.unitPrice === undefined ? '' : String(it.unitPrice),
        amount: String(it.amount),
        note: it.note ?? '',
        salesId: it.salesId ?? '',
        salesLabel: it.salesLabel ?? it.salesId ?? '',
        itemCode: it.itemCode ?? '',
        itemLabel: it.itemCode ? (itemPickLabel(it.itemName, it.itemGroup) || it.itemCode) : '',
        newItemGroup: it.itemCode ? '' : (it.newItemGroup ?? ''),
        newItemName: it.itemCode ? '' : (it.newItemName ?? ''),
        newItemSpec: it.itemCode ? '' : (it.newItemSpec ?? ''),
      };
      // 아직 아무도 분류하지 않은 행에만 — 사람이 비워 둔 칸을 되살리지 않으려고
      const untouched = !row.account && !row.subAccount;
      const hint = d.cardVendorHints?.[it.vendor ?? ''] ?? null;
      return isCard && untouched ? withCardDefaults(row, vehicleLabel, authorName, hint) : row;
    }),
  };
}

function formToBody(form: FormState, submit: boolean, keepAllItems = false): api.ExpenseDocFields {
  return {
    docDate: form.docDate || undefined,
    purchaseType: form.purchaseType.trim() || null,
    spendCategory: (form.spendCategory || null) as api.SpendCategory | null,
    purpose: form.purpose.trim() || null,
    vendor: form.vendor.trim() || null,
    payMethod: form.payMethod.trim() || null,
    payDateText: form.payDateText.trim() || null,
    // 지급일 — 비우면 null(지움 · 사용자 「비우는 것도 가능하게」) · 법인카드는 서버가 버린다 (v2.267.0)
    payDate: form.payDate || null,
    bankHolder: form.bankHolder.trim() || null,
    bankName: form.bankName.trim() || null,
    bankAccount: form.bankAccount.trim() || null,
    cardNo: form.cardNo.trim() || null,
    periodFrom: form.periodFrom || null,
    periodTo: form.periodTo || null,
    items: form.items
      .filter((it) =>
        keepAllItems
        || it.itemDate || it.account || it.vendor.trim() || it.description.trim()
        || it.amount.trim() || it.note.trim())
      .map((it) => ({
        itemDate: it.itemDate || null,
        account: (it.account || null) as api.ExpenseAccount | null,
        subAccount: it.subAccount || null,
        subAuto: it.subAuto && !!it.subAccount,
        usageTarget: it.usageTarget.trim() || null,
        vendor: it.vendor.trim() || null,
        description: it.description.trim() || null,
        qty: it.qty.trim() === '' ? null : Number(it.qty),
        unitPrice: it.unitPrice.trim() === '' ? null : Number(it.unitPrice),
        amount: Math.round(Number(it.amount) || 0),
        note: it.note.trim() || null,
        // 판매물품 줄의 영업건 (v2.246.0) — 다른 분류면 보내지 않는다(서버도 버린다)
        salesId: form.spendCategory === '판매물품' ? (it.salesId || null) : null,
        // 판매물품 줄의 매입 품목 (v2.304.0 · 개발요청 #16) — 기존 품목이면 코드만, 직접 입력이면 세 칸
        ...(form.spendCategory === '판매물품'
          ? (it.itemCode
            ? { itemCode: it.itemCode }
            : { itemCode: null, newItemGroup: it.newItemGroup || null, newItemName: it.newItemName.trim() || null, newItemSpec: it.newItemSpec.trim() || null })
          : {}),
      })),
    // 수출입관련이면 고른 수출입건 · 아니면 빈 목록(서버가 연결을 지운다) (v2.230.0)
    importOrderIds: form.spendCategory === '수출입관련'
      ? form.importOrders.map((o) => o.importOrderId).filter((n): n is number => n !== null)
      : [],
    submit,
  };
}

/* ── 결재판 ─────────────────────────────────────────── */
function ApprovalCells({ cells, backfillNote }: {
  cells: api.ExpenseLineCell[];
  /** 소급 등록 문서 — 포털 밖에서 결재가 끝나 도장 칸이 비어 있는 게 정상이다 */
  backfillNote?: string | null;
}) {
  if (backfillNote) {
    return (
      <div className="border border-amber-300 bg-amber-50 rounded px-3 py-2 text-[11.5px] leading-relaxed">
        <span className="px-1 py-0.5 rounded border border-amber-300 bg-white text-amber-700 text-[11px] font-semibold">소급</span>
        <span className="ml-1.5 font-semibold text-amber-800">포털 밖에서 결재가 끝난 문서</span>
        <div className="text-amber-700 mt-0.5">{backfillNote}</div>
        <div className="text-amber-600/80 mt-0.5">
          포털에서 승인을 누른 사람이 없어 결재란은 비워 둡니다.
        </div>
      </div>
    );
  }
  return (
    <table className="border-collapse text-center">
      <thead>
        <tr>
          {cells.map((c) => (
            <th key={c.role}
              className="border border-slate-300 bg-slate-50 text-xs font-semibold text-slate-500 px-3 py-1 w-[84px]">
              {c.role}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {cells.map((c) => (
            <td key={c.role} className="border border-slate-300 h-[52px] align-middle">
              {c.skipped ? (
                <span className="text-xs text-slate-500">생략</span>
              ) : c.stamped ? (
                <div className="flex flex-col items-center justify-center gap-0.5">
                  {c.userId && c.hasSignature ? (
                    <img src={signatureImageUrl(c.userId)} alt={c.name}
                      className="max-h-[30px] max-w-[70px] object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <span className="text-[13px] font-semibold text-slate-700">{c.name}</span>
                  )}
                  <span className="text-[11px] text-slate-500 leading-none">
                    {c.stampedAt ? c.stampedAt.slice(2, 10).replace(/-/g, '.') : ''}
                  </span>
                </div>
              ) : null}
            </td>
          ))}
        </tr>
        <tr>
          {cells.map((c) => (
            <td key={c.role} className="border border-slate-300 text-[10.5px] text-slate-500 px-1 py-0.5">
              {c.skipped ? '' : c.name}
              {/* 대리결재 표기 — 지정 결재자가 아닌 사람이 승인한 칸 (v2.100.0) */}
              {!c.skipped && c.delegated && (
                <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>
              )}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

/* ── 보조과목 검색칸 (법인카드) — v2.93.0 ─────────────
 *
 * 「식대」·「주유」처럼 **내용을 치면** 그 보조과목이 뜨고, 고르면 계정과목이 따라 붙는다.
 *
 * ⭐목록에 없는 글자는 **확정되지 않는다** — 칸을 벗어나면 마지막으로 고른 값으로 되돌아간다
 *   (사용자 지시: "리스트에 없으면 입력 안 되도록. 리스트에서 검색해서 들어가도록").
 * ⚠드롭다운은 position:fixed 로 띄운다 — 표 래퍼가 overflow-auto 라 absolute 로 두면
 *   칸 안에 갇혀 잘린다. 열 때 칸 위치를 재고, 표가 스크롤되면 닫는다.
 * ⚠옵션은 onMouseDown 으로 고른다 — onClick 은 blur 가 먼저 닫아 버려 안 눌린다.
 * ⭐v2.226.1 — **목록 자기 안의 스크롤은 닫지 않는다.** 스크롤 감지가 캡처 단계(화면 전체)라
 *   43개 목록을 휠로 한 칸만 내려도 닫혔다(사용자 신고 10-02). 목록 끝에서 더 굴려도 뒤 페이지로
 *   번지지 않게 overscroll-contain(뒤 목록 화면이 길면 번져서 닫혔다 · 실측) ·
 *   닫힌 뒤 칸을 다시 누르면 열린다(포커스가 칸에 남아 있으면 onFocus 가 다시 안 불린다).
 *   (목록 스크롤바를 끌어도 크롬은 칸의 포커스를 안 뺏는다 — 실측 · 따로 막지 않음)
 */
function SubAccountCell({ value, account, onPick, disabled }: {
  value: string;
  account: string;
  onPick: (opt: api.SubAccountOption | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const opts = api.searchSubAccounts(open ? q : '');

  const place = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 180) });
  };

  // 표가 스크롤되거나 창이 바뀌면 자리가 어긋난다 — 그냥 닫는다 (목록 자기 안의 스크롤만 빼고)
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      const t = e.target;
      if (t instanceof Node && listRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const commit = (o: api.SubAccountOption) => {
    onPick(o);
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
  };

  return (
    <>
      <input
        ref={inputRef}
        disabled={disabled}
        className="w-full border-0 bg-transparent px-1 py-0.5 text-[12.5px] outline-none focus:bg-sky-50"
        value={open ? q : value}
        placeholder="식대·주유…"
        title={value ? `${value} — ${account}` : '내용을 치면 계정과목이 따라옵니다'}
        onFocus={() => { setQ(''); setHi(0); place(); setOpen(true); }}
        // 표 스크롤로 닫힌 뒤 같은 칸을 다시 누르면 연다 — 포커스가 남아 있어 onFocus 는 다시 안 온다
        onClick={() => { if (!open) { setQ(''); setHi(0); place(); setOpen(true); } }}
        onChange={(e) => { setQ(e.target.value); setHi(0); place(); setOpen(true); }}
        // ⭐자유 입력은 버린다 — 목록에서 고른 것만 값이 된다
        onBlur={() => { setOpen(false); setQ(''); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, opts.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') {
            e.preventDefault();
            const o = opts[hi];
            if (o) commit(o);
          } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setQ(''); inputRef.current?.blur(); }
          else if (e.key === 'Backspace' && q === '' && value) { onPick(null); }
        }}
      />
      {open && rect && (
        <div
          ref={listRef}
          className="fixed z-50 max-h-[240px] overflow-auto overscroll-contain rounded border border-slate-300 bg-white shadow-lg text-[12.5px]"
          style={{ left: rect.left, top: rect.top, width: rect.width }}
        >
          {opts.length === 0 ? (
            <div className="px-2 py-1.5 text-slate-400">
              그런 보조과목이 없습니다 — 목록에 있는 것만 고를 수 있습니다
            </div>
          ) : opts.map((o, i) => (
            <div
              key={`${o.account}/${o.sub}`}
              className={`flex items-baseline justify-between gap-2 px-2 py-1 cursor-pointer ${
                i === hi ? 'bg-sky-100' : 'hover:bg-slate-50'
              }`}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); commit(o); }}
            >
              <span className="text-slate-800">{o.sub}</span>
              <span className="text-xs text-slate-500 shrink-0">{o.account}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ── 직원 선택 (복리후생비 사용대상) — 작은 오버레이 ── */
function EmployeePicker({
  employees, value, onChange, onClose,
}: {
  employees: { userId: string; name: string; department: string | null }[];
  value: string;
  onChange: (next: string) => void;
  onClose: () => void;
}) {
  const selected = new Set(value.split(',').map((s) => s.trim()).filter(Boolean));
  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    // 직원 목록 순서 유지해서 합치기
    const ordered = employees.map((e) => e.name).filter((n) => next.has(n));
    const extras = [...next].filter((n) => !employees.some((e) => e.name === n));
    onChange([...ordered, ...extras].join(','));
  };
  /** 전체 선택·해제 (v2.93.0) — 회식처럼 부서 전원이 대상일 때 하나씩 누르지 않게 */
  const selectAll = () => onChange(employees.map((e) => e.name).join(','));
  const clearAll = () => onChange('');
  // 배경 클릭으로는 닫지 않는다 — 아래 [확인] 로만 (check:modal · v2.146.0)
  return (
    <div className="fixed inset-0 z-[85] bg-black/20 flex items-center justify-center">
      <div className="bg-white rounded shadow-xl border border-slate-200 w-[300px] max-h-[70vh] flex flex-col">
        <div className="px-4 py-2.5 border-b border-slate-200 text-sm font-semibold text-slate-700">
          직원 선택 <span className="text-xs font-normal text-slate-500">(사용대상)</span>
        </div>
        <div className="px-4 py-1.5 border-b border-slate-100 flex items-center gap-1.5">
          <button type="button" onClick={selectAll}
            className="btn btn-secondary btn-sm">
            전체 선택
          </button>
          <button type="button" onClick={clearAll}
            className="btn btn-secondary btn-sm">
            전체 해제
          </button>
          <span className="ml-auto text-xs text-slate-500">{selected.size}명</span>
        </div>
        <div className="overflow-y-auto px-4 py-2">
          {employees.map((e) => (
            <label key={e.userId} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
              <input type="checkbox" checked={selected.has(e.name)} onChange={() => toggle(e.name)} />
              <span>{e.name}</span>
              <span className="text-xs text-slate-500">{e.department ?? ''}</span>
            </label>
          ))}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-200 flex justify-end">
          <button type="button" onClick={onClose}
            className="btn btn-primary">확인</button>
        </div>
      </div>
    </div>
  );
}

export function ExpenseDocModal({ docId, docType, onClose, onChanged, startInEdit }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // 개인경비 [새 정산서(복사)] — 기존 문서 내용으로 새 문서를 만드는 모드 (v2.51.0)
  const [asNew, setAsNew] = useState(false);
  const isNew = docId === null || asNew;
  const [mode, setMode] = useState<'view' | 'edit'>(docId === null || startInEdit ? 'edit' : 'view');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [pickerKey, setPickerKey] = useState<number | null>(null);
  // 판매물품 — 매입 품목 고르기 창을 연 줄(행 key) (v2.304.0 · 개발요청 #16)
  const [itemPickFor, setItemPickFor] = useState<number | null>(null);
  // 판매물품 — 영업건 고르기 창을 연 줄(행 key) · 'all' = 모든 줄에 한 번에 (v2.246.0)
  const [salesPickFor, setSalesPickFor] = useState<number | 'all' | null>(null);
  // 개인경비 — 작성 화면에서 고른 영수증. 문서가 저장된 뒤에 올린다 (v2.52.0)
  const [pendingReceipts, setPendingReceipts] = useState<File[]>([]);
  // 💸 송금완료 창 (v2.267.0) · 저장 뒤 알림 한 줄
  const [remitOpen, setRemitOpen] = useState(false);
  const [remitNotice, setRemitNotice] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['expense', 'doc', docId],
    queryFn: () => api.getExpenseDoc(docId!),
    enabled: docId !== null,
  });
  const detail = detailQuery.data ?? null;
  /**
   * ⚠휴일근무일지·기술지원팀월간보고서는 **전용 모달**을 쓴다 — 여기로는 오지 않는다.
   *   그래도 타입상 올 수 있게 됐으므로(결재함이 여러 종류를 함께 받는다) 명시적으로 걸러 둔다.
   *   v2.97.1 · v2.113.0 (기술지원팀월간보고서 추가)
   * ⭐「지출 3종인가」로 판정한다 — 빼기(≠ 휴일근무일지)로 적으면 종류를 늘릴 때마다
   *   새 종류가 조용히 이 모달로 흘러든다.
   */
  const loadedType = detail?.doc.docType;
  const isSpendingType = (t: api.ApprovalDocType | undefined): t is api.ExpenseDocType =>
    !!t && (api.EXPENSE_DOC_TYPES as api.ApprovalDocType[]).includes(t);
  const type: api.ExpenseDocType = asNew
    ? '개인경비정산서'
    : (isSpendingType(loadedType) ? loadedType : (docType ?? '구입지급결의서'));

  const isCard = type === '법인카드사용내역서';
  const isPurchase = type === '구입지급결의서';
  const isPersonal = type === '개인경비정산서';
  // 첨부 종류 — 서버의 EXPENSE_ATTACH_KIND 와 같은 표(새 문서는 서버 응답이 없어 여기서 정한다)
  const attachKind = api.expenseAttachKind(type);
  const attachLabel = api.EXPENSE_ATTACH_LABEL[type];
  // 카드 문서 — 업로드 원본에서 만든 문서는 일자·사용처·금액 잠금 + 행 추가·삭제 불가
  const cardLocked = isCard && !asNew && (detail?.doc.statementId ?? null) !== null;
  /** 작성자에게 배정된 차량번호 (v2.93.0) — 차량유지비 행의 **사용대상** 기본값 (v2.109.0 비고에서 뺌). 없으면 null */
  const myVehicle = isCard ? (detail?.authorVehicle?.label ?? null) : null;
  /** 작성자 이름 (v2.93.0) — 복리후생비 행의 사용대상(함께한 직원) 기본값 = 본인 */
  const myName = isCard ? (detail?.doc.authorName?.trim() || null) : null;

  /**
   * 「열었을 때의 폼」 지문 — 닫기 전에 지금 폼과 비교한다 (v2.146.0).
   * 폼을 **새로 불러오는 자리**(아래 useEffect · [이 내용으로 새 정산서])에서만 다시 찍는다.
   * 사람이 칸을 고치는 setForm 에서는 건드리지 않는다 — 그래야 「달라졌나」가 읽힌다.
   */
  const baselineRef = useRef<string | null>(null);
  if (baselineRef.current === null) baselineRef.current = formFingerprint(form);

  // 기존 문서를 편집 모드로 열 때 폼 초기화
  useEffect(() => {
    if (detail && mode === 'edit' && !asNew) {
      const f = detailToForm(detail);
      setForm(f);
      baselineRef.current = formFingerprint(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.doc.id, detail?.doc.updatedAt, mode]);

  /** 작성 중인 내용이 저장 안 된 채로 있나 — 보기 모드에서는 언제나 false */
  const isDirty = (): boolean =>
    mode === 'edit' && (formFingerprint(form) !== baselineRef.current || pendingReceipts.length > 0);

  /**
   * 닫기·취소 전에 물어본다 (v2.146.0 · 사용자 요청 「변경사항이 있습니다 …」).
   * 바뀐 게 없으면 묻지 않고 그대로 닫는다 — 그냥 열어만 본 창까지 붙잡지 않게.
   */
  const confirmDiscard = (what: '닫' | '취소'): boolean => {
    if (!isDirty()) return true;
    const 무엇 = what === '닫' ? '닫으면' : '취소하면';
    return confirm(
      `작성 중인 내용이 있습니다.\n${무엇} 저장하지 않은 내용이 사라집니다.\n\n그래도 ${what === '닫' ? '닫을' : '취소할'}까요?`,
    );
  };
  /** × 로 창 닫기 — 작성 중이면 한 번 물어본다 */
  const closeGuarded = (): void => { if (confirmDiscard('닫')) onClose(); };

  // 직원 목록 (복리후생비 사용대상 체크박스)
  // v2.55.2: 사람 아닌 계정(공용·시스템·샘플) 제외는 서버 /expense/users 가 책임진다.
  const employeesQuery = useQuery({
    queryKey: ['expense', 'users'],
    queryFn: api.listPortalUsers,
    staleTime: 300_000,
    enabled: isCard && mode === 'edit',
  });
  const employees = employeesQuery.data ?? [];

  const changed = () => {
    qc.invalidateQueries({ queryKey: ['expense'] });
    qc.invalidateQueries({ queryKey: ['approvals', 'pendingCount'] });
    onChanged?.();
  };

  const saveMut = useMutation({
    mutationFn: async (v: { submit: boolean; src: FormState }) => {
      const body = formToBody(v.src, v.submit, cardLocked);
      const r = isNew
        ? await api.createExpenseDoc({ ...body, docType: type })
        : await api.updateExpenseDoc(docId!, body);
      // 작성 화면에서 고른 영수증 — 문서가 생긴 뒤에 올린다 (v2.52.0)
      if (attachKind) for (const f of pendingReceipts) await api.uploadReceipt(r.id, f, attachKind);
      return r;
    },
    onSuccess: async (r, v) => {
      setError(null);
      setPendingReceipts([]);
      closePdfPreview();
      changed();
      if (v.submit) {
        alert(`제출되었습니다 (${r.docNo}) — 결재선을 따라 승인이 진행됩니다.`);
        onClose();
      } else if (isNew) {
        alert(`임시저장되었습니다 (${r.docNo}) — [사내결재 문서함] 메뉴에서 이어서 작성할 수 있습니다.`);
        onClose();
      } else {
        setMode('view');
        await detailQuery.refetch();
      }
    },
    onError: (e: Error) => { closePdfPreview(); setError(e.message); },
  });

  /** 제출 전 검사 — 서버 validateForSubmit 과 같은 규칙을 미리보기 단계에서 먼저 걸러 준다 */
  const validateForPreview = (body: api.ExpenseDocFields): void => {
    if (body.items.length === 0) throw new Error('내역이 1행 이상 필요합니다');
    if (isPurchase) {
      if (!body.purpose?.trim()) throw new Error('목적을 입력하세요');
      if (!body.spendCategory) throw new Error('지출 분류를 선택하세요');
      if (body.spendCategory === '수출입관련' && (body.importOrderIds ?? []).length === 0) {
        throw new Error('수출입관련 지출은 어느 수출입건의 비용인지 골라 주세요 (🚢 수출입건)');
      }
      const i = body.items.findIndex((it) => !it.description?.trim());
      if (i >= 0) throw new Error(`${i + 1}번째 행의 품목을 입력하세요`);
      return;
    }
    const noDate = body.items.findIndex((it) => !it.itemDate);
    if (noDate >= 0) throw new Error(`${noDate + 1}번째 행의 일자를 입력하세요`);
    const noAcct = body.items.findIndex((it) => !it.account);
    if (noAcct >= 0) throw new Error(`${noAcct + 1}번째 행의 계정과목을 선택하세요`);
    if (isPersonal && (!body.bankHolder?.trim() || !body.bankName?.trim() || !body.bankAccount?.trim())) {
      throw new Error('환급 계좌(예금주·은행·계좌번호)를 입력하세요');
    }
    if (isCard) {
      const badSub = body.items.findIndex((it) => !it.subAccount?.trim());
      if (badSub >= 0) throw new Error(`${badSub + 1}번째 행의 보조과목을 선택하세요`);
    }
  };

  /**
   * [확인] — 저장 없이 실제 제출본과 같은 PDF 를 만들어 보여주고, 그 미리보기 안에서
   * 제출 여부를 고르게 한다 (v2.51.0 구입지급 → v2.52.0 3종 공통).
   * ⭐dryRun 원칙 — 미리보기와 실제 제출본이 다른 코드를 타지 않게 서버의 같은 렌더러 사용.
   */
  const previewMut = useMutation({
    mutationFn: async (src: FormState) => {
      const body = formToBody(src, false, cardLocked);
      validateForPreview(body);
      return api.previewExpensePdf({
        ...body,
        docType: type,
        statementId: detail?.doc.statementId ?? null,
        // 이미 저장된 문서를 고치는 중이면 그 문서의 첨부까지 붙여서 보여 준다 (v2.73.0).
        // 방금 고른 파일(pendingReceipts)은 아직 서버에 없어 미리보기에 안 나온다.
        previewDocId: isNew ? null : docId,
      });
    },
    onSuccess: (blobUrl, src) => {
      setError(null);
      openPdfPreview({
        url: blobUrl,
        downloadUrl: blobUrl,
        filename: `${type}_제출전_미리보기.pdf`,
        mime: 'application/pdf',
        confirm: {
          message: '이대로 제출하면 결재선(부서장 → 경영지원 → 대표이사)으로 올라갑니다.',
          confirmLabel: '제출',
          cancelLabel: '돌아가서 수정',
          onConfirm: async () => {
            // 실패하면 saveMut.onError 가 미리보기를 닫고 모달에 사유를 띄운다
            try { await saveMut.mutateAsync({ submit: true, src }); } catch { /* 표시 완료 */ }
          },
        },
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  // ⚠state 로 두면 setState 직후 mutate 가 옛 값을 읽는다 — ref 로 동기 전달
  const recallReasonRef = useRef<string | undefined>(undefined);
  const actionMut = useMutation({
    mutationFn: async (kind: 'approve' | 'reject' | 'recall' | 'delete') => {
      if (kind === 'approve') return api.approveExpenseDoc(docId!);
      if (kind === 'reject') return api.rejectExpenseDoc(docId!, rejectReason.trim() || undefined);
      if (kind === 'recall') return api.recallExpenseDoc(docId!, recallReasonRef.current);
      await api.deleteExpenseDoc(docId!);
      return null;
    },
    onSuccess: async (r, kind) => {
      setError(null);
      setRejectOpen(false);
      setRejectReason('');
      changed();
      if (kind === 'delete') { onClose(); return; }
      await detailQuery.refetch();
      // 최종승인 회수로 거두지 못한 매입 (v2.246.0) — 사람이 손댄 매입은 남겨 두었다
      const kept = r?.purchaseKept ?? [];
      if (kind === 'recall' && kept.length > 0) {
        window.alert(`승인을 회수했습니다.\n\n매입관리에 남겨 둔 매입: ${kept.map((k) => `#${k.purchaseId}(${k.why})`).join(', ')}`);
      }
    },
    onError: (e: Error) => setError(e.message),
  });

  const receiptMut = useMutation({
    mutationFn: async (files: File[]) => {
      if (attachKind) for (const f of files) await api.uploadReceipt(docId!, f, attachKind);
    },
    onSuccess: async () => { changed(); await detailQuery.refetch(); },
    onError: (e: Error) => setError(e.message),
  });

  /* 📦 판매물품 → 매입관리 연동 (v2.132.0 · v2.246.0). 되돌리기를 한 쌍으로 둔다.
   *   v2.246.0: 최종승인 때 서버가 자동으로 한다 — 이 단추는 자동이 못 한 줄을 다시 시도하는 길.
   *   같은 물품으로 보이는 매입이 여럿이라 못 한 줄이 있으면, 한 번 더 물어보고 그래도 새로 만든다(force). */
  const linkMut = useMutation({
    mutationFn: (force: boolean) => api.linkExpensePurchases(docId!, force),
    onSuccess: async (r, force) => {
      changed();
      await detailQuery.refetch();
      const summary = r.summary
        ?? `매입 ${r.created}건을 등록했습니다${r.skipped ? ` (이미 연동된 ${r.skipped}행은 건너뜀)` : ''}.`;
      const ambiguous = (r.held ?? []).filter((h) => (h.candidates ?? []).length > 1);
      if (!force && ambiguous.length > 0) {
        if (window.confirm(
          `${summary}\n\n같은 물품으로 보이는 매입이 여럿이라 고르지 못한 줄이 ${ambiguous.length}개 있습니다.\n`
          + '매입관리에서 그 매입들을 먼저 확인하시고, 정말 새로 사신 물품이면 [확인]을 눌러 새로 만드세요.',
        )) linkMut.mutate(true);
        return;
      }
      window.alert(`${summary}\n\n입고일은 물건이 들어오면 매입관리에서 채워 주세요${(r.needsReview ?? r.created) > 0 ? ' · 「확인필요」로 들어간 줄은 품목도 채워 주세요' : ''}.`);
    },
    onError: (e: Error) => setError(e.message),
  });
  const unlinkMut = useMutation({
    mutationFn: () => api.unlinkExpensePurchases(docId!),
    onSuccess: async (r) => {
      changed();
      await detailQuery.refetch();
      const keptMsg = r.kept.length
        ? `\n\n남겨 둔 매입: ${r.kept.map((k) => `#${k.purchaseId}(${k.why})`).join(', ')}`
        : '';
      const restoredMsg = r.restored ? ` · 이어 붙였던 매입 ${r.restored}건은 지우지 않고 연결만 풀었습니다` : '';
      window.alert(`매입 ${r.removed}건을 지우고 연동을 해제했습니다${restoredMsg}.${keptMsg}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  // 합계 (편집 중 실시간) — 전부 부가세 포함가 기준 (v2.51.0)
  const editTotal = useMemo(
    () => form.items.reduce((s, it) => s + (Number(it.amount) || 0), 0),
    [form],
  );

  const setItem = (key: number, patch: Partial<FormItem>) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => {
        if (it.key !== key) return it;
        const next = { ...it, ...patch };
        // 계정과목이 바뀌면 보조과목·사용대상은 다시 고른다 (분류·입력 방식이 달라짐)
        if ('account' in patch && patch.account !== it.account) {
          next.subAccount = '';
          next.usageTarget = '';
        }
        // 구입 — 수량·단가가 둘 다 있으면 금액 자동 계산
        if (isPurchase && ('qty' in patch || 'unitPrice' in patch)) {
          const q = Number(next.qty); const u = Number(next.unitPrice);
          if (next.qty.trim() !== '' && next.unitPrice.trim() !== ''
              && Number.isFinite(q) && Number.isFinite(u)) {
            next.amount = String(Math.round(q * u));
          }
        }
        return next;
      }),
    }));
  };

  /**
   * 법인카드 보조과목 고르기 (v2.93.0) — **보조과목이 계정과목을 데려온다**.
   *
   * 종전엔 계정과목을 먼저 고르고 그 안에서 보조과목을 골랐다. 직원들이 엑셀에서 하던 대로
   * 「식대」·「주유」를 바로 치면 계정과목이 따라오게 뒤집은 것이다(2026-08-25 사용자 지시).
   *   · 목록에 있는 쌍만 들어온다 — 자유 입력 글자는 확정되지 않는다(호출부가 막는다).
   *   · 사용대상 입력 방식이 달라지는 계정과목으로 바뀔 때만 사용대상을 비운다
   *     (차량유지비→접대비 처럼 뜻이 달라지는 칸에 옛 값이 남으면 안 된다).
   *   · 차량유지비로 정해지면 배정 차량번호를 **사용대상 빈 칸에만** 넣는다 (비고는 v2.109.0 부터 안 넣음).
   */
  const pickSubAccount = (key: number, opt: api.SubAccountOption | null) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => {
        if (it.key !== key) return it;
        // 사람이 고르거나 지우면 「자동」 표시는 내린다 (v2.227.0)
        if (!opt) return { ...it, subAccount: '', account: '', usageTarget: '', subAuto: false, subAutoWhy: '' };
        const next: FormItem = { ...it, subAccount: opt.sub, account: opt.account, subAuto: false, subAutoWhy: '' };
        if (api.usageTargetMode(it.account || null) !== api.usageTargetMode(opt.account)) {
          next.usageTarget = '';
        }
        return withCardDefaults(next, myVehicle, myName);
      }),
    }));
  };

  // 사용내용 blur → 계정과목 자동추천 (개인경비만 — 카드는 구조화된 선택으로 대체)
  const suggestFor = (key: number, description: string) => {
    if (!isPersonal) return;
    const s = api.suggestAccount(description);
    if (!s) return;
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => (it.key === key && !it.account ? { ...it, account: s } : it)),
    }));
  };

  const viewingDoc = asNew ? null : (detail?.doc ?? null);

  const openDocPdf = () => {
    if (!detail?.attachmentId) return;
    openServerPdfPreview({
      pdfUrl: api.attachmentDownloadUrl(detail.attachmentId),
      filename: `${detail.doc.docNo}_${detail.doc.docType}.pdf`,
    });
  };

  const inputCls = 'ui-input w-full';
  const cellInputCls = 'ui-input ui-input-sm w-full';
  const lockedCellCls = 'px-1.5 py-1 text-[12.5px] text-slate-600 whitespace-nowrap';

  /* ── 편집: 항목 표 (종류별 3분기) ─────────────────── */
  const renderItemsEdit = () => {
    if (isPurchase) {
      // 판매물품이면 줄마다 영업건 칸 (v2.246.0) — 비면 「재고 보유」
      const sellMode = form.spendCategory === '판매물품';
      return (
        <>
          <div className="overflow-x-auto">
            <table className={`w-full text-[12.5px] border-collapse ${sellMode ? 'min-w-[940px]' : 'min-w-[700px]'}`}>
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="border border-slate-200 px-1 py-1 w-[30px]">순번</th>
                  <th className="border border-slate-200 px-1 py-1 min-w-[200px]">품목 *</th>
                  <th className="border border-slate-200 px-1 py-1 w-[58px]">수량</th>
                  <th className="border border-slate-200 px-1 py-1 w-[90px]">단가</th>
                  <th className="border border-slate-200 px-1 py-1 w-[100px]">금액</th>
                  {sellMode && <th className="border border-slate-200 px-1 py-1 w-[180px] min-w-[180px]">영업건 <span className="font-normal text-slate-400">(비면 재고)</span></th>}
                  {sellMode && <th className="border border-slate-200 px-1 py-1 w-[150px] min-w-[150px]" title="매입관리에 들어갈 품목 — 목록에 없으면 직접 입력">매입 품목 *</th>}
                  <th className="border border-slate-200 px-1 py-1 w-[100px]">비고</th>
                  <th className="border border-slate-200 px-1 py-1 w-[30px]"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={it.key}>
                    <td className="border border-slate-200 text-center text-slate-400">{i + 1}</td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.description}
                        onChange={(e) => setItem(it.key, { description: e.target.value })} />
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={`${cellInputCls} text-right`} inputMode="numeric" value={it.qty}
                        onChange={(e) => setItem(it.key, { qty: e.target.value.replace(/[^\d.]/g, '') })} />
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={`${cellInputCls} text-right`} inputMode="numeric" value={it.unitPrice}
                        onChange={(e) => setItem(it.key, { unitPrice: e.target.value.replace(/[^\d.]/g, '') })} />
                    </td>
                    <td className={`border border-slate-200 p-0.5 ${qtyPriceMismatch(it.qty, it.unitPrice, it.amount) !== null ? 'bg-amber-100' : ''}`}
                      title={qtyPriceMismatch(it.qty, it.unitPrice, it.amount) !== null ? '수량×단가와 금액이 다릅니다' : undefined}>
                      <input className={`${cellInputCls} text-right`} inputMode="numeric" value={it.amount}
                        onChange={(e) => setItem(it.key, { amount: e.target.value.replace(/[^\d-]/g, '') })} />
                    </td>
                    {sellMode && (
                      <td className="border border-slate-200 p-0.5 min-w-[180px]">
                        {it.salesId ? (
                          <div className="flex items-center gap-1 px-1">
                            <button type="button" title={`${it.salesLabel || it.salesId} — 눌러서 다른 영업건 고르기`}
                              className="flex-1 min-w-0 text-left truncate text-sky-800 hover:underline"
                              onClick={() => setSalesPickFor(it.key)}>{it.salesLabel || it.salesId}</button>
                            <button type="button" aria-label="영업건 빼기 (재고 보유로)" title="영업건 빼기 (재고 보유로)"
                              className="text-slate-400 hover:text-rose-600 px-0.5"
                              onClick={() => setItem(it.key, { salesId: '', salesLabel: '' })}>×</button>
                          </div>
                        ) : (
                          <button type="button" className="w-full text-left px-1 py-0.5 text-slate-500 hover:text-sky-700 whitespace-nowrap"
                            onClick={() => setSalesPickFor(it.key)}>
                            재고 보유 <span className="text-sky-600">· 영업건 고르기</span>
                          </button>
                        )}
                      </td>
                    )}
                    {sellMode && (
                      <td className="border border-slate-200 p-0.5 min-w-[150px]" data-item-pick-cell={it.itemCode || (it.newItemName ? 'new' : '')}>
                        {it.itemCode || it.newItemName ? (
                          <div className="flex items-center gap-1 px-1">
                            <button type="button"
                              title={it.itemCode
                                ? `${it.itemLabel || it.itemCode} — 눌러서 다른 품목 고르기`
                                : `새 품목(최종승인 때 만듦): ${itemPickLabel(it.newItemName, it.newItemGroup)}${it.newItemSpec ? ` · ${it.newItemSpec}` : ''} — 눌러서 다시 고르기`}
                              className={`flex-1 min-w-0 text-left truncate hover:underline ${it.itemCode ? 'text-emerald-800' : 'text-amber-800'}`}
                              onClick={() => setItemPickFor(it.key)}>
                              {it.itemCode ? (it.itemLabel || it.itemCode) : <>새 품목 · {it.newItemName}</>}
                            </button>
                            <button type="button" aria-label="매입 품목 빼기" title="매입 품목 빼기"
                              className="text-slate-400 hover:text-rose-600 px-0.5"
                              onClick={() => setItem(it.key, { itemCode: '', itemLabel: '', newItemGroup: '', newItemName: '', newItemSpec: '' })}>×</button>
                          </div>
                        ) : (
                          <button type="button" className="w-full text-left px-1 py-0.5 text-amber-700 hover:text-sky-700 whitespace-nowrap"
                            onClick={() => setItemPickFor(it.key)}>
                            + 품목 고르기
                          </button>
                        )}
                      </td>
                    )}
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.note}
                        onChange={(e) => setItem(it.key, { note: e.target.value })} />
                    </td>
                    <td className="border border-slate-200 text-center">
                      <button type="button" className="text-slate-300 hover:text-rose-500" title="행 삭제"
                        onClick={() => setForm((f) => ({
                          ...f,
                          items: f.items.length === 1 ? [newItem()] : f.items.filter((x) => x.key !== it.key),
                        }))}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <button type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setForm((f) => ({ ...f, items: [...f.items, newItem()] }))}>
                + 행 추가
              </button>
              {sellMode && form.items.length > 1 && (
                <button type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setSalesPickFor('all')}>
                  모든 줄에 같은 영업건
                </button>
              )}
            </div>
            <div className="text-sm text-slate-600">
              합계 <b className="num text-slate-900">{fmtKrw(editTotal)}</b>원
              <span className="text-xs text-slate-500 ml-1">(부가세 포함)</span>
            </div>
          </div>
          <QtyPriceWarn
            lines={form.items.map((it, i) => ({ no: i + 1, calc: qtyPriceMismatch(it.qty, it.unitPrice, it.amount), amount: Number(it.amount) || 0 }))}
            note="맞게 적은 것이면(예: 반반 부담) 그대로 올리셔도 됩니다."
          />
        </>
      );
    }

    if (isPersonal) {
      return (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse min-w-[760px]">
              <thead>
                <tr className="bg-slate-50 text-slate-500">
                  <th className="border border-slate-200 px-1 py-1 w-[30px]">순번</th>
                  <th className="border border-slate-200 px-1 py-1 w-[118px]">일자 *</th>
                  <th className="border border-slate-200 px-1 py-1 w-[104px]">계정과목 *</th>
                  <th className="border border-slate-200 px-1 py-1 min-w-[120px]">상호</th>
                  <th className="border border-slate-200 px-1 py-1 min-w-[150px]">사용내용</th>
                  <th className="border border-slate-200 px-1 py-1 w-[96px]">금액</th>
                  <th className="border border-slate-200 px-1 py-1 w-[96px]">비고</th>
                  <th className="border border-slate-200 px-1 py-1 w-[30px]"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={it.key}>
                    <td className="border border-slate-200 text-center text-slate-400">{i + 1}</td>
                    <td className="border border-slate-200 p-0.5">
                      <DateInput value={it.itemDate} onChange={(v) => setItem(it.key, { itemDate: v })}
                        inputClassName={cellInputCls} hidePicker />
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <select className={cellInputCls} value={it.account}
                        onChange={(e) => setItem(it.key, { account: e.target.value })}>
                        <option value=""></option>
                        {api.EXPENSE_ACCOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.vendor}
                        onChange={(e) => setItem(it.key, { vendor: e.target.value })} />
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.description}
                        placeholder="예: 식대 (홍길동,김철수)"
                        onChange={(e) => setItem(it.key, { description: e.target.value })}
                        onBlur={(e) => suggestFor(it.key, e.target.value)} />
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={`${cellInputCls} text-right`} inputMode="numeric" value={it.amount}
                        onChange={(e) => setItem(it.key, { amount: e.target.value.replace(/[^\d-]/g, '') })} />
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.note}
                        onChange={(e) => setItem(it.key, { note: e.target.value })} />
                    </td>
                    <td className="border border-slate-200 text-center">
                      <button type="button" className="text-slate-300 hover:text-rose-500" title="행 삭제"
                        onClick={() => setForm((f) => ({
                          ...f,
                          items: f.items.length === 1 ? [newItem()] : f.items.filter((x) => x.key !== it.key),
                        }))}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2">
            <button type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setForm((f) => ({ ...f, items: [...f.items, newItem()] }))}>
              + 행 추가
            </button>
            <div className="text-sm text-slate-600">
              합계 <b className="num text-slate-900">{fmtKrw(editTotal)}</b>원
            </div>
          </div>
        </>
      );
    }

    // 법인카드 — 일자·사용처·금액 잠금, 행 추가·삭제 불가 (업로드 원본이 정답)
    // v2.56.1 스크롤 구조: 안내문·계정과목 안내판·표 제목행은 고정, 내역 행만 스크롤.
    //   모달 본문(isCard && edit 일 때 overflow-hidden flex-col) 안에서 이 fragment 의
    //   표 래퍼만 flex-1 + overflow-auto 가 되고, thead th 는 그 안에서 sticky top-0.
    //   ⚠고정 영역에는 전부 shrink-0 — flex 아이템은 기본이 '줄어듦' 이라 안 붙이면
    //     화면이 낮을 때 안내판·메타가 찌그러진다(스크롤 대신 압축돼 버림).
    //   ⚠표 래퍼는 min-h — 안내판이 커도 스크롤 칸이 0 으로 무너지지 않게.
    //   (표를 overflow-x-auto 로 감싼 채 바깥 스크롤에 sticky 를 걸면 안 붙는다 —
    //    세로·가로 스크롤을 같은 래퍼(overflow-auto)에 모아야 제목행이 가로로도 따라간다)
    const thCls = 'sticky top-0 z-10 bg-slate-50 border border-slate-200 shadow-[inset_0_-1px_0_#cbd5e1] px-1 py-1';
    /** 「자동」 표시가 붙은 행 수 — 안내문 한 줄 (v2.227.0) */
    const autoCount = form.items.filter((it) => it.subAuto).length;
    return (
      <>
        <div className="shrink-0 text-xs text-slate-500 mb-1.5">
          일자·사용처·금액은 경영지원부가 올린 카드사 내역 그대로라 수정할 수 없습니다.
          <b className="text-slate-600"> 보조과목</b>에 내용(식대·주유…)을 치면
          <b className="text-slate-600"> 계정과목은 자동</b>으로 정해집니다 — 이어서 사용대상(생략 가능)·적요를 채워 주세요.
          {(myVehicle || myName) && (
            <span className="text-sky-700">
              {' '}미리 채워지는 것(고칠 수 있습니다):
              {myVehicle && <> 차량유지비 → 사용대상에 <b>{myVehicle}</b></>}
              {myVehicle && myName && ' ·'}
              {myName && <> 복리후생비 → 사용대상에 <b>{myName}</b></>}
            </span>
          )}
          {autoCount > 0 && (
            <span data-sub-auto-note className="text-amber-700">
              {' '}🤖 <b>자동 {autoCount}줄</b> = 사용처를 보고 채운 값 · 다르면(접대비 등) 다시 고르세요.
            </span>
          )}
        </div>
        {/* 계정과목 상시 안내 (v2.55.3) — 샘플 엑셀 우측의 '계정과목별 내용' 표를 작성 화면에 항상 표시
            (사용자 요청 2026-08-06). 내용은 보조과목 드롭다운과 같은 원천(EXPENSE_SUB_ACCOUNTS)이라
            어긋나지 않는다. 확정본 PDF 에는 넣지 않는다(사용자 확정). */}
        <div className="shrink-0 mb-2 rounded border border-sky-200 bg-sky-50/60 px-2.5 py-1.5">
          <div className="text-xs font-semibold text-sky-800 mb-1">
            📖 계정과목 안내 — 아래 내용(보조과목) 중 하나를 보조과목 칸에 치면 그 과목이 붙습니다
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-[3px]">
            {api.EXPENSE_ACCOUNTS.map((a) => {
              const subs = (api.EXPENSE_SUB_ACCOUNTS[a] ?? []).filter((s) => s !== '기타');
              const tMode = api.usageTargetMode(a);
              const target =
                tMode === 'employees' ? '함께한 직원' : api.USAGE_TARGET_PLACEHOLDER[a];
              return (
                <div key={a} className="flex items-baseline gap-1.5 text-[11.5px] leading-snug">
                  <span className="shrink-0 w-[68px] font-medium text-slate-700">{a}</span>
                  <span className="text-slate-600">
                    {subs.length > 0 ? subs.join(' · ') : '위 과목에 속하지 않는 그 밖의 지출'}
                    {target && <span className="text-sky-700"> — 사용대상: {target}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex-1 min-h-[140px] overflow-auto">
          <table className="w-full text-[12.5px] border-collapse min-w-[860px]">
            <thead>
              <tr className="text-slate-500">
                <th className={`${thCls} w-[30px]`}>순번</th>
                <th className={`${thCls} w-[80px]`}>일자</th>
                {/* 칸 위치는 종전 그대로(계정과목이 왼쪽) — 채우는 순서만 뒤집혔다 (v2.93.0) */}
                <th className={`${thCls} w-[84px]`}>계정과목</th>
                <th className={`${thCls} w-[104px]`}>보조과목 *</th>
                <th className={`${thCls} min-w-[110px]`}>사용처</th>
                <th className={`${thCls} w-[150px]`}>사용대상</th>
                <th className={`${thCls} min-w-[130px]`}>적요</th>
                <th className={`${thCls} w-[84px]`}>금액</th>
                {/* 차량번호(「21호 0620」)가 들어가므로 종전 70px 로는 잘린다 — 1280 에서 실측 (v2.93.0) */}
                <th className={`${thCls} w-[96px]`}>비고</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const tMode = api.usageTargetMode(it.account || null);
                return (
                  <tr key={it.key}>
                    <td className="border border-slate-200 text-center text-slate-400">{i + 1}</td>
                    <td className={`border border-slate-200 ${lockedCellCls}`}>{it.itemDate}</td>
                    {/* 계정과목은 보조과목을 고르면 저절로 채워진다 — 직접 고르지 않는다 (v2.93.0)
                        빈 칸 글자는 「—」(v2.227.0 · 예전 「자동」은 새 「자동」 표시와 헷갈려 바꿈) */}
                    <td className={`border border-slate-200 px-1 py-0.5 text-[12.5px] ${
                      it.account ? 'bg-slate-50 text-slate-700' : 'bg-slate-50 text-slate-300'
                    }`} title={it.account ? undefined : '보조과목을 고르면 저절로 채워집니다'}>
                      {it.account || '—'}
                    </td>
                    {/* 🤖 자동 (v2.227.0) — 포털이 사용처를 보고 채웠고 사람이 아직 안 고른 칸. 다시 고르면 사라진다 */}
                    <td className={`border border-slate-200 p-0.5 ${it.subAuto ? 'bg-amber-50' : ''}`}>
                      <div className="flex items-center gap-0.5">
                        <div className="flex-1 min-w-0">
                          <SubAccountCell
                            value={it.subAccount}
                            account={it.account}
                            onPick={(opt) => pickSubAccount(it.key, opt)}
                          />
                        </div>
                        {it.subAuto && (
                          <span
                            data-sub-auto
                            title={`${it.subAutoWhy || '사용처를 보고 포털이 자동으로 채운 값'} — 다르면(접대비 등) 보조과목 칸에서 다시 고르세요`}
                            className="shrink-0 rounded border border-amber-300 bg-amber-100 px-1 text-[11px] leading-[15px] font-semibold text-amber-800 cursor-help"
                          >
                            자동
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`border border-slate-200 ${lockedCellCls}`}
                      title={it.vendor}>{it.vendor}</td>
                    <td className="border border-slate-200 p-0.5">
                      {tMode === 'employees' ? (
                        <button type="button"
                          className="w-full text-left border border-slate-200 rounded px-1.5 py-0.5 text-[12px] text-slate-700 hover:bg-slate-50 truncate"
                          title={it.usageTarget || '직원 선택'}
                          onClick={() => setPickerKey(it.key)}>
                          {it.usageTarget || <span className="text-slate-400">직원 선택…</span>}
                        </button>
                      ) : tMode === 'text' ? (
                        <input className={cellInputCls} value={it.usageTarget}
                          placeholder={api.USAGE_TARGET_PLACEHOLDER[it.account] ?? ''}
                          onChange={(e) => setItem(it.key, { usageTarget: e.target.value })} />
                      ) : (
                        <span className="block text-center text-slate-300">—</span>
                      )}
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.description}
                        onChange={(e) => setItem(it.key, { description: e.target.value })} />
                    </td>
                    <td className={`border border-slate-200 ${lockedCellCls} text-right num`}>
                      {fmtKrw(Number(it.amount) || 0)}
                    </td>
                    <td className="border border-slate-200 p-0.5">
                      <input className={cellInputCls} value={it.note}
                        onChange={(e) => setItem(it.key, { note: e.target.value })} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="shrink-0 text-right text-sm text-slate-600 mt-2">
          합계 <b className="num text-slate-900">{fmtKrw(editTotal)}</b>원
        </div>
        {pickerKey !== null && (
          <EmployeePicker
            employees={employees}
            value={form.items.find((it) => it.key === pickerKey)?.usageTarget ?? ''}
            onChange={(next) => setItem(pickerKey, { usageTarget: next })}
            onClose={() => setPickerKey(null)}
          />
        )}
      </>
    );
  };

  /* ── 첨부 상자 — 개인경비정산서=영수증 / 구입지급결의서=견적서 (v2.73.0) ───────
   * queue=true(작성 화면): 고른 파일을 담아 뒀다가 저장·제출할 때 함께 올린다.
   *   새 문서는 아직 문서번호가 없어서 바로 올릴 수 없고, 작성을 취소하면 파일도 남지 않는다.
   * queue=false(보기 화면): 이미 있는 문서라 즉시 올린다. */
  const renderReceipts = (queue: boolean) => (
    <ExpenseAttachBox
      label={attachLabel}
      uploaded={asNew ? [] : (detail?.receipts ?? [])}
      pending={pendingReceipts}
      queue={queue}
      canAdd={queue || (detail?.canEdit ?? false)}
      canDelete={detail?.canEdit ?? false}
      pendingWhen={isPurchase ? '제출할 때 올라감' : '저장할 때 올라감'}
      onPick={(files) => {
        if (queue) setPendingReceipts((p) => [...p, ...files]);
        else receiptMut.mutate(files);
      }}
      onRemovePending={(i) => setPendingReceipts((p) => p.filter((_, j) => j !== i))}
      onDelete={(r) => {
        api.deleteReceipt(r.id)
          .then(() => detailQuery.refetch())
          .catch((e: Error) => setError(e.message));
      }}
    />
  );

  /* ── 편집 폼 ──────────────────────────────────────── */
  /**
   * 지급일 (v2.267.0 · 개발 계획 #19) — 구입지급결의서·개인경비정산서 공용. 비워 둘 수 있다(사용자 「비우는 것도 가능하게」).
   * 옛 문서의 「결제날짜」 글(「선결재」 등)은 입력칸이 없어진 대신 아래에 그대로 보여 주고, 저장해도 지워지지 않는다.
   */
  const payDateField = (
    <label className="ui-label">지급일
      <DateInput value={form.payDate} onChange={(v) => setForm({ ...form, payDate: v })}
        inputClassName={inputCls} placeholder="비워 둬도 됩니다" />
      {form.payDateText.trim() && (
        <span className="flex items-center gap-1.5 text-xs text-slate-500 font-normal mt-0.5"
          title="옛 결제날짜 칸에 적힌 글 — 그대로 남습니다. 필요 없으면 [지우기]">
          <span className="truncate">예전 결제날짜: {form.payDateText.trim()}</span>
          {/* 입력칸이 없어진 옛 글을 지울 길 (v2.268.0 · 재검토 결함 3) — 저장해야 지워진다 */}
          <button type="button" className="text-rose-600 hover:underline shrink-0"
            onClick={(e) => { e.preventDefault(); setForm({ ...form, payDateText: '' }); }}>
            지우기
          </button>
        </span>
      )}
    </label>
  );
  // 카드 편집(v2.56.1): 메타·안내판·표 제목행 고정 + 내역만 스크롤 → flex 사슬로 높이를 내린다.
  const renderEdit = () => (
    <div className={isCard ? 'flex-1 min-h-0 flex flex-col space-y-4' : 'space-y-4'}>
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3${isCard ? ' shrink-0' : ''}`}>
        <label className="ui-label">작성일자
          <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} inputClassName={inputCls} />
        </label>
        {isPurchase && (
          <>
            <label className="ui-label">구분
              <input className={inputCls} value={form.purchaseType} placeholder="구입 / 지급"
                onChange={(e) => setForm({ ...form, purchaseType: e.target.value })} />
            </label>
            {/* 지출 분류 (v2.132.0) — 「판매물품 → 매입」·「수출입관련 → 수출입 비용」의 갈림길.
                구분(구입/지급)과는 다른 칸이라 나란히 둔다. */}
            <label className="ui-label">지출 분류 *
              <select className={inputCls} value={form.spendCategory}
                onChange={(e) => setForm({ ...form, spendCategory: e.target.value })}>
                <option value="">선택하세요</option>
                {api.SPEND_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            {form.spendCategory === '수출입관련' && (
              <div className="col-span-2 md:col-span-4">
                <ImportOrderPicker value={form.importOrders} onChange={(next) => setForm({ ...form, importOrders: next })} />
              </div>
            )}
            {/* 판매물품 → 매입 자동 (v2.246.0) — 작성자가 무엇이 어디로 가는지 알고 쓰게 */}
            {form.spendCategory === '판매물품' && (
              <div className="col-span-2 md:col-span-4 border border-sky-200 bg-sky-50/60 rounded p-2.5 text-xs text-slate-700 leading-relaxed">
                <b className="text-sky-800">📦 판매할 물품</b> — 최종승인되면 아래 내역이 줄마다 <b>매입관리에 자동으로</b> 올라갑니다.
                {' '}줄마다 <b>영업건</b>을 고르면 그 영업건의 매입으로, 고르지 않으면 <b>재고 보유</b>로 들어갑니다.
                {' '}줄마다 <b>매입 품목</b>도 골라 주세요(목록에 없으면 직접 입력 · 상신할 때 꼭 있어야 합니다) — 품목을 고른 줄은 매입관리에 <b>「확인필요」 없이</b> 들어갑니다.
                <div className="text-xs text-slate-500 mt-0.5">
                  매입 금액은 부가세를 뺀 공급가(÷1.1)로 들어갑니다 · 매입관리에 이미 손으로 적은 같은 물품(같은 구매처·같은 금액)이 있으면 새로 만들지 않고 그 줄과 잇습니다 · 입고일은 물건이 들어오면 경영지원이 매입관리에서 채웁니다.
                </div>
              </div>
            )}
            <label className="ui-label col-span-2">목적 *
              <input className={inputCls} value={form.purpose} placeholder="예: 사무용 키보드 고장으로 구매"
                onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            </label>
            <label className="ui-label">구매처
              <input className={inputCls} value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
            </label>
            <label className="ui-label">결제방법
              <input className={inputCls} value={form.payMethod} placeholder="예: 계좌이체, 지로납부, 선결재"
                onChange={(e) => setForm({ ...form, payMethod: e.target.value })} />
            </label>
            {/* 지급일 (v2.267.0 · 개발 계획 #19) — 옛 「결제날짜」 글 칸을 날짜 칸으로(사용자 결정 10-15). 비워 둘 수 있다 */}
            {payDateField}
            <div className="text-xs text-slate-500 self-end pb-1.5">※ 금액은 전부 부가세 포함가</div>
          </>
        )}
        {isPersonal && (
          <>
            <label className="ui-label">예금주 *
              <input className={inputCls} value={form.bankHolder}
                onChange={(e) => setForm({ ...form, bankHolder: e.target.value })} />
            </label>
            <label className="ui-label">은행 *
              <input className={inputCls} value={form.bankName}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
            </label>
            <label className="ui-label">계좌번호 *
              <input className={inputCls} value={form.bankAccount}
                onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} />
            </label>
            {payDateField}
          </>
        )}
        {isCard && (
          <>
            <label className="ui-label">사용카드
              <input className={inputCls} value={form.cardNo} disabled={cardLocked}
                onChange={(e) => setForm({ ...form, cardNo: e.target.value })} />
            </label>
            {cardLocked ? (
              /* 사용기간 = 귀속월 1일~말일 자동 (v2.52.0) — 카드내역서는 1개월 단위 제출 */
              <div className="text-xs text-slate-500 col-span-2">사용기간
                <div className={`${inputCls} bg-slate-50 text-slate-600`}>
                  {form.periodFrom || '-'} ~ {form.periodTo || '-'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  카드 사용 월의 1일~말일로 자동 지정됩니다
                </div>
              </div>
            ) : (
              <>
                <label className="ui-label">사용기간(부터)
                  <DateInput value={form.periodFrom} onChange={(v) => setForm({ ...form, periodFrom: v })} inputClassName={inputCls} />
                </label>
                <label className="ui-label">사용기간(까지)
                  <DateInput value={form.periodTo} onChange={(v) => setForm({ ...form, periodTo: v })} inputClassName={inputCls} />
                </label>
              </>
            )}
          </>
        )}
      </div>
      {renderItemsEdit()}
      {/* 첨부 — 개인경비=영수증 / 구입지급=견적서. 작성 화면에서 바로 고른다 (v2.52.0 · v2.73.0) */}
      {attachKind !== null && renderReceipts(true)}
    </div>
  );

  /* ── 보기 ─────────────────────────────────────────── */
  const renderView = () => {
    if (!detail || !viewingDoc) return <div className="text-sm text-slate-400 py-10 text-center"><LoadingLines /></div>;
    const d = viewingDoc;
    const its = detail.items;
    const linkedCount = its.filter((it) => it.purchaseId != null).length;
    const matchedCount = its.filter((it) => it.purchaseId != null && it.purchaseLink === 'matched').length;
    // 판매물품 — 줄마다 어느 영업건(또는 재고 보유)으로 가나 (v2.246.0)
    const sellView = isPurchase && d.spendCategory === '판매물품';
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <table className="text-[12.5px]">
            <tbody>
              <tr>
                <td className="text-slate-400 pr-2 py-0.5">작성자</td>
                <td className="pr-6">{d.authorName} {d.authorTitle ?? ''} ({d.authorDept ?? '부서 없음'})</td>
                <td className="text-slate-400 pr-2">작성일</td>
                <td>{d.docDate}</td>
              </tr>
              <tr>
                <td className="text-slate-400 pr-2 py-0.5">문서번호</td>
                <td className="pr-6">{d.docNo}</td>
                <td className="text-slate-400 pr-2">합계</td>
                <td className="num font-semibold">
                  {fmtKrw(d.totalAmount)}원{isPurchase && <span className="text-xs text-slate-500 font-normal"> (부가세 포함)</span>}
                </td>
              </tr>
              {isPurchase && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5">목적</td>
                  <td colSpan={3}>{d.purpose ?? '-'}</td>
                </tr>
              )}
              {isPurchase && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5">구매처</td>
                  <td className="pr-6">{d.vendor ?? '-'}</td>
                  <td className="text-slate-400 pr-2">결제방법</td>
                  <td>{d.payMethod || '-'}</td>
                </tr>
              )}
              {isPurchase && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5">구분</td>
                  <td className="pr-6">{d.purchaseType ?? '-'}</td>
                  <td className="text-slate-400 pr-2">지출 분류</td>
                  <td>{d.spendCategory ?? '-'}</td>
                </tr>
              )}
              {/* 수출입건 — 결재자가 어느 건의 비용인지 보고 결재하게 (v2.230.0 · 개발 요청 #14) */}
              {isPurchase && (detail.importOrders ?? []).length > 0 && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5 align-top">수출입건</td>
                  <td colSpan={3}>
                    {(detail.importOrders ?? []).map((o) => (
                      <div key={`${o.importOrderId}-${o.refNo}`}><b className="text-violet-800">{o.refNo}</b> <span className="text-slate-600">{o.summary}</span></div>
                    ))}
                  </td>
                </tr>
              )}
              {isPersonal && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5">환급계좌</td>
                  <td colSpan={3}>{[d.bankName, d.bankAccount, d.bankHolder].filter(Boolean).join(' / ') || '-'}</td>
                </tr>
              )}
              {/* 💸 지급일(작성자) · 송금(경영지원) — 구입지급·개인경비 (v2.267.0 · 개발 계획 #19) */}
              {(isPurchase || isPersonal) && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5">지급일</td>
                  <td className="pr-6">{d.payDateShown || '-'}</td>
                  <td className="text-slate-400 pr-2">송금</td>
                  <td>
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      {/* 상태는 서버가 정한 remitState 로만 (v2.268.0) — 목록 칸과 같은 부품 */}
                      <RemitStateLabel state={d.remitState} remitDate={d.remitDate} remitByName={detail.remitByName} payDate={d.payDate} />
                      {detail.canMarkRemit && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setRemitNotice(null); setRemitOpen(true); }}>
                          {d.remitDate ? '송금일 고치기' : '☑ 송금완료 표시'}
                        </button>
                      )}
                    </span>
                    {remitNotice && <div className="text-xs text-emerald-700 mt-0.5">{remitNotice}</div>}
                  </td>
                </tr>
              )}
              {isCard && (
                <tr>
                  <td className="text-slate-400 pr-2 py-0.5">사용카드</td>
                  <td className="pr-6">{d.cardNo ?? '-'}</td>
                  <td className="text-slate-400 pr-2">기간</td>
                  <td>{d.periodFrom ?? '-'} ~ {d.periodTo ?? '-'}</td>
                </tr>
              )}
              {d.rejectReason && (
                <tr>
                  <td className="text-rose-500 pr-2 py-0.5">반려사유</td>
                  <td colSpan={3} className="text-rose-600">{d.rejectReason}</td>
                </tr>
              )}
            </tbody>
          </table>
          <ApprovalCells cells={detail.cells} backfillNote={d.backfillNote} />
        </div>

        <div className="overflow-x-auto">
          <table className={`w-full text-[12.5px] border-collapse ${sellView ? 'min-w-[850px]' : 'min-w-[680px]'}`}>
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="border border-slate-200 px-1.5 py-1 w-[32px]">순번</th>
                {!isPurchase && <th className="border border-slate-200 px-1.5 py-1 w-[86px]">일자</th>}
                {!isPurchase && <th className="border border-slate-200 px-1.5 py-1 w-[80px]">계정과목</th>}
                {isCard && <th className="border border-slate-200 px-1.5 py-1 w-[76px]">보조과목</th>}
                {!isPurchase && <th className="border border-slate-200 px-1.5 py-1">{isCard ? '사용처' : '상호'}</th>}
                {isCard && <th className="border border-slate-200 px-1.5 py-1">사용대상</th>}
                <th className="border border-slate-200 px-1.5 py-1">{isPurchase ? '품목' : isCard ? '적요' : '사용내용'}</th>
                {isPurchase && <th className="border border-slate-200 px-1.5 py-1 w-[52px]">수량</th>}
                {isPurchase && <th className="border border-slate-200 px-1.5 py-1 w-[84px]">단가</th>}
                <th className="border border-slate-200 px-1.5 py-1 w-[92px]">금액</th>
                {sellView && <th className="border border-slate-200 px-1.5 py-1 w-[210px] min-w-[170px]">판매 연결</th>}
                <th className="border border-slate-200 px-1.5 py-1 w-[70px]">비고</th>
              </tr>
            </thead>
            <tbody>
              {its.map((it, i) => (
                <tr key={it.seq}>
                  <td className="border border-slate-200 px-1.5 py-1 text-center text-slate-400">{i + 1}</td>
                  {!isPurchase && <td className="border border-slate-200 px-1.5 py-1 text-center whitespace-nowrap">{it.itemDate ?? ''}</td>}
                  {!isPurchase && <td className="border border-slate-200 px-1.5 py-1 text-center">{it.account ?? ''}</td>}
                  {isCard && <td className="border border-slate-200 px-1.5 py-1 text-center">{it.subAccount ?? ''}</td>}
                  {!isPurchase && <td className="border border-slate-200 px-1.5 py-1">{it.vendor ?? ''}</td>}
                  {isCard && <td className="border border-slate-200 px-1.5 py-1">{it.usageTarget ?? ''}</td>}
                  <td className="border border-slate-200 px-1.5 py-1">{it.description ?? ''}</td>
                  {isPurchase && <td className="border border-slate-200 px-1.5 py-1 text-right num">{it.qty ?? ''}</td>}
                  {isPurchase && <td className="border border-slate-200 px-1.5 py-1 text-right num">{it.unitPrice !== null && it.unitPrice !== undefined ? fmtKrw(it.unitPrice) : ''}</td>}
                  <td className={`border border-slate-200 px-1.5 py-1 text-right num ${isPurchase && qtyPriceMismatch(it.qty, it.unitPrice, it.amount) !== null ? 'bg-amber-100' : ''}`}
                    title={isPurchase && qtyPriceMismatch(it.qty, it.unitPrice, it.amount) !== null ? '수량×단가와 금액이 다릅니다' : undefined}>{fmtKrw(it.amount)}</td>
                  {sellView && (
                    <td className="border border-slate-200 px-1.5 py-1">
                      {it.salesId
                        ? <span className="text-sky-800" title={it.salesLabel ?? it.salesId}>{it.salesLabel ?? it.salesId}</span>
                        : <span className="text-slate-500">재고 보유</span>}
                      {/* 매입 품목 (v2.304.0 · 개발요청 #16) — 결재자도 무엇으로 들어갈지 보게 */}
                      <div className="text-xs" data-item-pick-view={it.itemCode ?? (it.newItemName ? 'new' : '')}>
                        {it.itemCode
                          ? <span className="text-emerald-800">품목: {itemPickLabel(it.itemName, it.itemGroup) || it.itemCode}</span>
                          : it.newItemName
                            ? <span className="text-amber-800">새 품목: {itemPickLabel(it.newItemName, it.newItemGroup)}{it.newItemSpec ? ` · ${it.newItemSpec}` : ''} (최종승인 때 만듦)</span>
                            : <span className="text-slate-400">결의서에 품목 없음 — 매입관리에서 채움</span>}
                      </div>
                      {it.purchaseId != null && (
                        <div className="text-xs text-slate-500">
                          → 매입 #{it.purchaseId} {it.purchaseLink === 'matched' ? '(이미 있던 매입에 이음)' : '(새로 만듦)'}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="border border-slate-200 px-1.5 py-1">{it.note ?? ''}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold">
                <td colSpan={isPurchase ? 4 : isCard ? 7 : 5} className="border border-slate-200 px-1.5 py-1 text-right">
                  합계{isPurchase ? ' (부가세 포함)' : ''}
                </td>
                <td className="border border-slate-200 px-1.5 py-1 text-right num">{fmtKrw(d.totalAmount)}</td>
                {sellView && <td className="border border-slate-200"></td>}
                <td className="border border-slate-200"></td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* 결재자도 같은 안내를 본다 (v2.229.3 · 점검 C#1 · 경고만) — PDF(인쇄본)에는 넣지 않는다 */}
        {isPurchase && (
          <QtyPriceWarn
            lines={its.map((it, i) => ({ no: i + 1, calc: qtyPriceMismatch(it.qty, it.unitPrice, it.amount), amount: Number(it.amount) || 0 }))}
            note="반반 부담처럼 일부러 다르게 적은 것일 수 있습니다 — 비고·첨부를 보고 판단해 주세요."
          />
        )}

        {/* 📦 판매물품 — 결재 중에는 「무엇이 어디로 갈지」 한 줄 (v2.246.0) */}
        {sellView && d.status !== '최종승인' && d.status !== '작성중' && d.status !== '반려' && (
          <div className="text-xs text-sky-800">
            📦 최종승인되면 위 「판매 연결」대로 매입관리에 자동으로 올라갑니다(공급가 ÷1.1 · 이미 손으로 적은 같은 매입이 있으면 그 줄과 잇습니다).
          </div>
        )}

        {/* 📦 판매물품 → 매입관리 연동 (v2.132.0 · v2.246.0 최종승인 때 자동)
            결재가 끝난 「판매물품」 문서만. 같은 내용을 매입에 다시 적지 않게 한다. */}
        {sellView && d.status === '최종승인' && (
          <div className="border border-sky-200 bg-sky-50/60 rounded p-3">
            <div className="text-xs font-bold text-sky-800 mb-1">📦 판매할 물품 — 매입관리 연동</div>
            <div className="text-xs text-slate-700">
              내역 {its.length}행 중 <b>{linkedCount}행</b>이 매입으로 갔습니다
              {linkedCount > 0 && <> (새로 만듦 {linkedCount - matchedCount} · 이미 있던 매입에 이음 {matchedCount})</>}.
              {linkedCount < its.length && (
                <div className="text-amber-700 mt-0.5">
                  ⚠ {its.length - linkedCount}행은 아직 매입으로 가지 않았습니다 — [📦 남은 줄 매입관리에 등록]을 누르면 다시 시도하고 이유를 알려 드립니다.
                </div>
              )}
              <div className="text-xs text-slate-500 mt-0.5">
                {its.some((it) => it.purchaseId != null && !it.itemCode)
                  ? '결의서에 품목이 없던 줄은 매입관리에 「확인필요」로 올라갑니다 — 거기서 품목을 채워 주세요. '
                  : '품목은 결의서에서 고른 대로 들어갔습니다. '}
                입고일은 물건이 들어오면 매입관리에서 채워 주세요. 금액은 부가세를 뺀 공급가입니다.
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              {linkedCount < its.length && (
                <button
                  type="button"
                  disabled={linkMut.isPending}
                  onClick={() => {
                    if (!window.confirm(
                      `남은 내역 ${its.length - linkedCount}행을 매입관리에 등록할까요?\n\n`
                      + '「판매 연결」대로 만들고, 이미 있던 같은 매입이 있으면 그 줄과 잇습니다. 잘못 만들었으면 [연동 해제] 로 되돌릴 수 있습니다.',
                    )) return;
                    linkMut.mutate(false);
                  }}
                  className="btn btn-primary btn-sm"
                >📦 남은 줄 매입관리에 등록</button>
              )}
              {linkedCount > 0 && (
                <button
                  type="button"
                  disabled={unlinkMut.isPending}
                  onClick={() => {
                    if (!window.confirm(
                      '매입 연동을 해제할까요?\n\n새로 만든 매입은 손대지 않았을 때만 지워집니다(확인 완료·품목·입출고가 생긴 매입은 그대로 둡니다).\n'
                      + '이미 있던 매입에 이은 줄은 지우지 않고, 이 문서가 바꾼 영업건만 되돌린 뒤 연결을 풉니다.',
                    )) return;
                    unlinkMut.mutate();
                  }}
                  className="btn btn-secondary btn-sm"
                >연동 해제</button>
              )}
            </div>
          </div>
        )}

        {/* 🚢 수출입관련 — 최종승인 때 자동으로 옮겨 둔다 (v2.132.0) */}
        {isPurchase && d.spendCategory === '수출입관련' && d.status === '최종승인' && (
          <div className="border border-violet-200 bg-violet-50/60 rounded p-3 text-xs text-slate-700">
            <span className="font-bold text-violet-800">🚢 수출입 비용으로 옮겨 두었습니다</span>
            {(detail.importOrders ?? []).length > 0
              ? <>{' '}— 고른 수출입건({(detail.importOrders ?? []).map((o) => o.refNo).join(', ')})과 이어 두었습니다(비어 있던 비용은 그 건들로 배분 · 국세청 계산서로 이미 들어온 같은 비용이면 그 줄에 합침). [수출입] 화면 「비용 내역」에서 이 문서번호로 확인할 수 있습니다.</>
              : <>{' '}— [수출입] 화면의 「비용 내역」에서 이 문서번호({d.docNo})로 찾아 수입 오더에 배분해 주세요.</>}
            <div className="text-xs text-slate-500 mt-0.5">
              배분하지 않으면 「미배분」으로 남습니다. 수출입 화면에서 [미배분만] 으로 걸러 볼 수 있습니다.
            </div>
          </div>
        )}

        {/* 첨부 — 개인경비=영수증 / 구입지급=견적서 (v2.51.0 · v2.73.0) */}
        {attachKind !== null && renderReceipts(false)}

        {/* 처리 이력 (v2.99.0) — 반려·회수 경위가 재제출 뒤에도 남는다 */}
        <DocHistoryList history={detail?.history} />

        {rejectOpen && (
          <div className="border border-rose-200 bg-rose-50 rounded p-3 space-y-2">
            <div className="text-xs font-semibold text-rose-600">반려 사유 (선택)</div>
            <textarea className="ui-input ui-textarea w-full border-rose-200" rows={2}
              value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="작성자에게 전달할 사유" />
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => setRejectOpen(false)}>취소</button>
              <button type="button" className="btn btn-danger-solid btn-sm"
                disabled={actionMut.isPending}
                onClick={() => actionMut.mutate('reject')}>반려 확정</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const status = viewingDoc?.status ?? '작성중';
  const canEditNow = isNew || (detail?.canEdit ?? false);
  // 개인경비 — 내 문서면 이 내용으로 새 정산서 (견적서 복사와 같은 개념, v2.51.0)
  const canCopy = !asNew && mode === 'view' && isPersonal
    && !!detail && detail.doc.authorId === user?.userId;

  const startCopy = () => {
    if (!detail) return;
    const f = detailToForm(detail, true);
    setForm(f);
    baselineRef.current = formFingerprint(f); // 복사본도 「열었을 때」가 기준이다 (v2.146.0)
    setAsNew(true);
    setMode('edit');
    setError(null);
    setPendingReceipts([]); // 영수증은 복사되지 않는다 (새로 첨부)
  };

  // ⛔배경(바깥) 클릭으로는 닫지 않는다 — 칸 안에서 글자를 드래그하다 창 밖에서 손을 떼도
  //   입력이 통째로 사라졌다(사용자 2026-09-09). 닫기는 × · [취소] 로만. check:modal 이 이 규칙을 지킨다.
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div
        className="bg-white rounded shadow-xl w-[1020px] max-w-[97vw] max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h3 className="ui-subsection-title">
              {type}{asNew ? ' 작성 (복사본)' : viewingDoc ? ` — ${viewingDoc.docNo}` : ' 작성'}
            </h3>
            {viewingDoc && !asNew && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${EXPENSE_STATUS_BADGE[status] ?? ''}`}>
                {status}
              </span>
            )}
            <span className="text-xs text-slate-500">
              작성자 본인과 결재선(부서장·경영지원·대표이사)만 볼 수 있습니다
            </span>
          </div>
          <button type="button" onClick={closeGuarded} aria-label="닫기"
            className="ui-close">×</button>
        </div>

        {/* 카드 편집만 내부 스크롤(내역 행 전용) — 그 외는 종전대로 본문 전체 스크롤 (v2.56.1) */}
        <div className={mode === 'edit' && isCard
          ? 'flex-1 min-h-0 flex flex-col overflow-hidden px-5 py-4'
          : 'overflow-y-auto px-5 py-4'}>
          {error && (
            <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2 whitespace-pre-line">
              {error}
            </div>
          )}
          {mode === 'edit' ? renderEdit() : renderView()}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b flex-wrap">
          <div className="flex gap-2">
            {mode === 'view' && detail?.attachmentId && (
              <button type="button" onClick={openDocPdf}
                className="btn btn-secondary">
                📄 PDF 미리보기
              </button>
            )}
            {/* 건별 엑셀 (v2.111.0) — 법인카드사용내역서만. 결재판·서명 없이 작성 내용만 */}
            {mode === 'view' && isCard && detail && (
              <button type="button"
                title="이 건의 사용내역을 엑셀로 내려받습니다 (서명 없이 작성된 내용만)"
                className="btn btn-success"
                onClick={async () => {
                  try { await api.downloadExpenseDocXlsx(detail.doc.id, detail.doc.docNo); }
                  catch (e) { setError((e as Error).message); }
                }}>
                📊 엑셀 내려받기
              </button>
            )}
            {canCopy && (
              <button type="button" onClick={startCopy}
                className="btn btn-outline">
                📋 이 내용으로 새 정산서
              </button>
            )}
            {mode === 'view' && (detail?.canDelete ?? false) && (
              <button type="button"
                className="btn btn-danger"
                disabled={actionMut.isPending}
                onClick={() => {
                  if (confirm(`${type} ${viewingDoc?.docNo} 을(를) 삭제할까요?\n첨부·영수증도 함께 삭제됩니다.`)) {
                    actionMut.mutate('delete');
                  }
                }}>
                삭제
              </button>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {mode === 'edit' ? (
              /* 3종 공통 (v2.52.0) — [확인]으로 미리보기를 띄우고 그 안에서 제출 여부를 고른다 */
              <>
                <button type="button"
                  onClick={() => {
                    // 작성 중인 내용이 있으면 한 번 물어본다 (v2.146.0) — [취소]도 내용을 버리는 자리다
                    if (!confirmDiscard('취소')) return;
                    setPendingReceipts([]); // 아직 안 올린 영수증은 버린다
                    if (isNew) onClose(); else setMode('view');
                  }}
                  className="btn btn-secondary">
                  취소
                </button>
                {!isPurchase && (
                  <button type="button" disabled={saveMut.isPending}
                    onClick={() => saveMut.mutate({ submit: false, src: form })}
                    className="btn btn-outline">
                    임시저장
                  </button>
                )}
                <button type="button" disabled={previewMut.isPending || saveMut.isPending}
                  onClick={() => previewMut.mutate(form)}
                  className="btn btn-primary">
                  {previewMut.isPending ? '미리보기 만드는 중…' : '확인 (미리보기 후 제출)'}
                </button>
              </>
            ) : (
              <>
                {canEditNow && (
                  <button type="button" onClick={() => setMode('edit')}
                    className="btn btn-secondary">
                    수정
                  </button>
                )}
                {(detail?.canSubmit ?? false) && (
                  /* 보기 화면에서 바로 제출할 때도 미리보기를 거친다 (v2.52.0) */
                  <button type="button" disabled={previewMut.isPending || saveMut.isPending}
                    onClick={() => { if (detail) previewMut.mutate(detailToForm(detail)); }}
                    className="btn btn-primary">
                    {previewMut.isPending ? '미리보기 만드는 중…' : '확인 (미리보기 후 제출)'}
                  </button>
                )}
                {(detail?.canApprove ?? false) && (
                  <button type="button" disabled={actionMut.isPending}
                    onClick={() => { if (confirm('승인할까요?')) actionMut.mutate('approve'); }}
                    className="btn btn-success-solid">
                    승인
                  </button>
                )}
                {(detail?.canReject ?? false) && (
                  <button type="button" disabled={actionMut.isPending}
                    onClick={() => setRejectOpen(true)}
                    className="btn btn-danger">
                    반려
                  </button>
                )}
                {(detail?.canRecall ?? false) && (
                  <button type="button" disabled={actionMut.isPending}
                    onClick={() => {
                      // ⭐완결 문서 회수는 사유 필수 (v2.101.0) — 서버도 거절하지만 화면에서 먼저 받는다
                      if (status === '최종승인') {
                        const r = window.prompt('완결(최종승인) 문서를 회수합니다.\n사유를 적어 주세요 (필수 — 처리 이력에 남습니다)');
                        if (r === null) return;
                        if (!r.trim()) { window.alert('사유가 없으면 완결 문서를 회수할 수 없습니다'); return; }
                        recallReasonRef.current = r.trim();
                        actionMut.mutate('recall');
                        return;
                      }
                      if (confirm('마지막 승인을 회수할까요? (한 단계 되돌림)')) {
                        recallReasonRef.current = undefined;
                        actionMut.mutate('recall');
                      }
                    }}
                    className="btn btn-warning">
                    승인 회수
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* 판매물품 줄의 영업건 고르기 (v2.246.0) — 이 창 **안에** 그려야 이 창(z-70) 위에 뜬다 */}
      <SalesPickerModal
        open={salesPickFor !== null}
        title={salesPickFor === 'all' ? '모든 줄에 넣을 영업건' : '이 물품이 갈 영업건'}
        onClose={() => setSalesPickFor(null)}
        onPick={(row) => {
          const patch = { salesId: row.salesId, salesLabel: salesRowLabel(row) };
          const target = salesPickFor;
          setForm((f) => ({
            ...f,
            items: f.items.map((it) => (target === 'all' || it.key === target ? { ...it, ...patch } : it)),
          }));
          setSalesPickFor(null);
        }}
      />
      {/* 판매물품 줄의 매입 품목 고르기 (v2.304.0 · 개발요청 #16) — 이 창 **안에** 그려야 이 창(z-70) 위에 뜬다 */}
      {itemPickFor !== null && (() => {
        const idx = form.items.findIndex((x) => x.key === itemPickFor);
        const row = idx >= 0 ? form.items[idx]! : null;
        if (!row) return null;
        return (
          <ItemPickModal
            rowNo={idx + 1}
            description={row.description}
            onClose={() => setItemPickFor(null)}
            onPick={(sel) => {
              setItem(row.key, sel.code
                ? { itemCode: sel.code, itemLabel: itemPickLabel(sel.name, sel.group) || sel.code, newItemGroup: '', newItemName: '', newItemSpec: '' }
                : { itemCode: '', itemLabel: '', newItemGroup: sel.group ?? '', newItemName: sel.name, newItemSpec: sel.spec ?? '' });
              setItemPickFor(null);
            }}
          />
        );
      })()}
      {/* 💸 송금완료 표시 (v2.267.0) — 이 창 **안에** 그려야 이 창(z-70) 위에 뜬다 */}
      {remitOpen && detail && (
        <ExpenseRemitModal
          target={{
            id: detail.doc.id, docNo: detail.doc.docNo, docType: detail.doc.docType,
            authorName: detail.doc.authorName, remitDate: detail.doc.remitDate ?? null,
          }}
          onClose={() => setRemitOpen(false)}
          onDone={(m) => { setRemitNotice(m); onChanged?.(); }}
        />
      )}
    </div>
  );
}

export default ExpenseDocModal;
